import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef,
} from "react";
import {
    IFilterParams,
    IFilterComp,
    IDoesFilterPassParams,
} from "ag-grid-community";
import { useSelector } from "react-redux";
import { IChannel, TActiveClientId } from "@/ts/common-types";
import { Switch } from "antd";
import { Input } from "antd";
import { debounce, map } from "underscore";

interface CustomSetFilterModel {
    values: string[];
    isAndMode: boolean;
    filterType: string;
}

interface CustomSetFilterProps extends IFilterParams {
    values: string[];
}

const CustomCampaignTagFilter = forwardRef<IFilterComp, CustomSetFilterProps>(
    (props, ref) => {
        const activeClientId = useSelector(
            (state: TActiveClientId) => state.activeClientId.value
        );
        const channel = useSelector((state: IChannel) => state.channel.value);

        const [isFilterRemoved, setIsFilterRemoved] = useState(false);
        const [isButtonClicked, setIsButtonClicked] = useState(false);
        const [isAndMode, setIsAndMode] = useState(false);
        const [selectedValues, setSelectedValues] = useState<string[]>([]);
        const guiRef = useRef<HTMLDivElement>(null);
        const [searchText, setSearchText] = useState("");
        const [availableTags, setAvailableTags] = useState<string[]>([]);

        useEffect(() => {
            const uniqueTags = new Set<string>();
            
            props.api.forEachLeafNode((node) => {
                const tags = map(node.data[props.colDef.field] || [], (tag: any) => `${tag.tag_id}##${tag.tag_name}`);
                tags.forEach((tag: string) => uniqueTags.add(tag));
            });

            const sortedTags = Array.from(uniqueTags).sort((a, b) => {
                const aName = a.split("##")[1];
                const bName = b.split("##")[1];
                return aName.localeCompare(bName);
            });
            setAvailableTags(sortedTags);
        }, [props.api, props.colDef.field]);

        const handleSearch = debounce((value: string) => {
            setSearchText(value.toLowerCase());
        }, 300);

        const filteredTags = availableTags.filter(tag => 
            tag.split("##")[1].toLowerCase().includes(searchText)
        );

        const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                setSelectedValues(filteredTags);
            } else {
                setSelectedValues([]);
            }
        };

        const areAllSelected = filteredTags.length > 0 && 
            filteredTags.every(tag => selectedValues.includes(tag));

        const onLogicToggle = () => {
            setIsAndMode(!isAndMode);
        };

        const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const newSelectedValues = [...selectedValues];
            if (newSelectedValues.includes(value)) {
                const index = newSelectedValues.indexOf(value);
                newSelectedValues.splice(index, 1);
            } else {
                newSelectedValues.push(value);
            }
            setSelectedValues(newSelectedValues);
        };

        const onApply = () => {
            setSelectedValues(selectedValues);
            setIsAndMode(isAndMode);
            setIsButtonClicked(true);
        };

        const onReset = () => {
            setSelectedValues([]);
            setIsAndMode(false);
            setIsButtonClicked(true);
        };

        useEffect(() => {
            if (isButtonClicked) {
                props.filterChangedCallback();
                setIsButtonClicked(false);
            }
        }, [
            selectedValues,
            isAndMode,
            activeClientId,
            channel,
            isButtonClicked,
            props,
        ]);

        const doesFilterPass = (params: IDoesFilterPassParams) => {
            const cellValue = params.data[props.colDef.field] || [];
            
            if (selectedValues.length === 0) {
                return true;
            }

            if (isAndMode) {
                return selectedValues.every((selectedTag) => {
                    const selectedTagId = selectedTag.split("##")[0];
                    return cellValue.some((cellTag: any) => cellTag.tag_id.toString() === selectedTagId);
                });
            }
            
            return selectedValues.some((selectedTag) => {
                const selectedTagId = selectedTag.split("##")[0];
                return cellValue.some((cellTag: any) => cellTag.tag_id.toString() === selectedTagId);
            });
        };

        const getModel = (): CustomSetFilterModel | null => {
            if (selectedValues.length === 0) {
                return null;
            }
            return {
                filterType: "set",
                values: selectedValues,
                isAndMode: isAndMode,
            };
        };

        const setModel = (model: CustomSetFilterModel | null): void => {
            if (!model) {
                setSelectedValues([]);
                setIsAndMode(false);
                setIsFilterRemoved(true);
            } else {
                setSelectedValues(model.values || []);
                setIsAndMode(model.isAndMode || false);
                setIsFilterRemoved(false);
                // Triggering custom filter change using isButtonClicked
                setIsButtonClicked(true);
            }
        };

        useEffect(() => {
            if (isFilterRemoved) {
                props.filterChangedCallback();
                setIsFilterRemoved(false);
            }
        }, [isFilterRemoved]);

        useImperativeHandle(ref, () => ({
            doesFilterPass,
            isFilterActive: () => selectedValues.length > 0,
            getModel,
            setModel,
            getGui: () => guiRef.current!,
            setFilterValues: (values: string[]) => {
                setSelectedValues(values);
                props.filterChangedCallback();
            },
        }));

        return (
            <div ref={guiRef} className="custom-filter-container">
                <div className="mb-3 d-flex align-items-center justify-content-left">
                    <Switch
                        size="small"
                        className="me-1"
                        checked={isAndMode}
                        onChange={onLogicToggle}
                    />
                    <label>AND Mode</label>
                </div>

                <Input
                    placeholder="Search tags..."
                    onChange={(e) => handleSearch(e.target.value)}
                    className="mb-2 border"
                />

                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <div className="custom-checkbox mb-1 d-flex align-items-center">
                        <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={areAllSelected}
                            style={{
                                width: "16px",
                                height: "16px",
                                marginRight: "5px",
                            }}
                        />
                        (Select All)
                    </div>

                    {filteredTags.map((value) => (
                        <div
                            className="custom-checkbox mb-1 d-flex align-items-center"
                            key={value}
                        >
                            <input
                                type="checkbox"
                                value={value}
                                onChange={onValueChange}
                                checked={selectedValues.includes(value)}
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                }}
                            />
                            {value.split("##")[1]}
                        </div>
                    ))}
                </div>
                <div className="border mt-2"></div>
                <div className="custom-filter-buttons">
                    <button
                        className="ag-standard-button ag-filter-apply-panel-button"
                        onClick={onReset}
                    >
                        Reset
                    </button>
                    <button
                        className="ag-standard-button ag-filter-apply-panel-button"
                        onClick={onApply}
                    >
                        Apply
                    </button>
                </div>
            </div>
        );
    }
);

export default CustomCampaignTagFilter; 